import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import api from '../../config/api';
import {
  FiGlobe,
  FiRefreshCw,
  FiPlay,
  FiSettings,
  FiCheckCircle,
  FiAlertCircle,
  FiActivity,
} from 'react-icons/fi';
import LoadingButton from '../LoadingButton';
import { formatScannerDate, parseRunSummary, runStatusClass } from './scannerUtils';
import { appAlert } from '../../utils/appDialog';
import ListPager from '../ListPager';
import { useClientPager } from '../../hooks/useClientPager';

function RunSummaryStats({ summary }) {
  if (!summary) return <span className="text-muted">—</span>;
  return (
    <div className="scanner-run-stats">
      <span title="Jobs inserted">{summary.inserted ?? 0} inserted</span>
      <span title="Total found">{summary.total_found ?? 0} found</span>
      <span title="Duplicates skipped">{summary.duplicates ?? 0} dupes</span>
      {summary.filtered_title != null && <span>{summary.filtered_title} title filtered</span>}
    </div>
  );
}

function RunDetailCard({ title, run, icon: Icon, variant = 'default' }) {
  const summary = parseRunSummary(run?.summary);
  return (
    <div className={`scanner-detail-card scanner-detail-card--${variant}`}>
      <div className="scanner-detail-card-head">
        {Icon && <Icon aria-hidden />}
        <h3>{title}</h3>
      </div>
      {!run ? (
        <p className="scanner-detail-empty">No runs recorded yet.</p>
      ) : (
        <dl className="scanner-detail-list">
          <div>
            <dt>Run ID</dt>
            <dd>#{run.id}</dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>
              <span className={`scanner-status-pill scanner-status-pill--${runStatusClass(run.status)}`}>
                {run.status}
              </span>
            </dd>
          </div>
          <div>
            <dt>Trigger</dt>
            <dd>{run.trigger_type || '—'}</dd>
          </div>
          <div>
            <dt>Started</dt>
            <dd>{formatScannerDate(run.started_at || run.created_at)}</dd>
          </div>
          <div>
            <dt>Completed</dt>
            <dd>{formatScannerDate(run.completed_at)}</dd>
          </div>
          {summary && (
            <div className="scanner-detail-span">
              <dt>Results</dt>
              <dd><RunSummaryStats summary={summary} /></dd>
            </div>
          )}
          {run.error_message && (
            <div className="scanner-detail-span">
              <dt>Error</dt>
              <dd className="scanner-error-text">{run.error_message}</dd>
            </div>
          )}
        </dl>
      )}
    </div>
  );
}

const ScannerDashboard = () => {
  const queryClient = useQueryClient();
  const [runBusy, setRunBusy] = useState(false);

  const { data: status, isLoading: statusLoading } = useQuery(
    ['scanner-status'],
    () => api.get('/scanner/status').then((r) => r.data),
    { refetchInterval: 5000 }
  );

  const { data: runs = [], isLoading: runsLoading } = useQuery(
    ['scanner-runs'],
    () => api.get('/scanner/runs?limit=100').then((r) => r.data),
    { refetchInterval: 10000 }
  );

  const {
    pageItems: pagedRuns,
    pagination: runsPagination,
    setPage: setRunsPage,
  } = useClientPager(runs);

  const handleRunNow = async () => {
    setRunBusy(true);
    try {
      const res = await api.post('/scanner/run');
      if (res.status === 202 || res.data?.status === 'started') {
        void appAlert('Scan started. Results will appear in run history shortly.');
      } else {
        void appAlert(res.data?.error || 'Scan request sent.');
      }
      queryClient.invalidateQueries(['scanner-runs']);
      queryClient.invalidateQueries(['scanner-status']);
      queryClient.invalidateQueries('jobs');
    } catch (e) {
      void appAlert(e.response?.data?.error || e.response?.data?.hint || e.message);
    } finally {
      setRunBusy(false);
    }
  };

  const refreshAll = () => {
    queryClient.invalidateQueries(['scanner-status']);
    queryClient.invalidateQueries(['scanner-runs']);
  };

  if (statusLoading) {
    return <div className="loading">Loading scanner status…</div>;
  }

  const serviceUp = status?.service?.reachable;
  const serviceRunning = status?.service?.running;
  const lastRun = status?.last_run;
  const lastSuccessfulRun = status?.last_successful_run;
  const config = status?.config;

  return (
    <div className="scanner-dashboard">
      <p className="scanner-dashboard-intro">
        Scans run in the independent scanner microservice (Docker + Postgres). GoDash only pushes
        config and triggers runs — jobs are stored in the scanner database. Configure connection and
        ATS sources in{' '}
        <Link to="/settings?tab=scanner" className="scanner-inline-link">
          Settings → Scanner configuration
        </Link>
        .
      </p>

      {!serviceUp && (
        <div className="scanner-service-alert" role="status">
          <FiAlertCircle aria-hidden />
          <div>
            <strong>Scanner service unreachable</strong>
            <p>
              {status?.service?.error
                || 'Start the Docker scanner and set SCANNER_SERVICE_URL / SCANNER_SECRET in backend config.'}
              {status?.service?.url ? (
                <>
                  {' '}Configured URL: <code>{status.service.url}</code>
                </>
              ) : null}
            </p>
          </div>
        </div>
      )}

      <div className="scanner-status-grid">
        <div className={`scanner-status-card${serviceUp ? ' is-ok' : ''}`}>
          <FiActivity aria-hidden />
          <div>
            <span className="scanner-status-card-label">Scanner service</span>
            <strong>{serviceUp ? 'Ready' : 'Unavailable'}</strong>
            {serviceRunning && <span className="scanner-live-badge">Scan in progress</span>}
          </div>
        </div>
        <div className="scanner-status-card">
          <FiGlobe aria-hidden />
          <div>
            <span className="scanner-status-card-label">Enabled sources</span>
            <strong>{status?.enabled_sources ?? 0}</strong>
          </div>
        </div>
        <div className="scanner-status-card">
          <FiSettings aria-hidden />
          <div>
            <span className="scanner-status-card-label">Schedule</span>
            <strong>{config?.schedule_enabled ? config.schedule_cron : 'Off'}</strong>
          </div>
        </div>
        <div className="scanner-status-card">
          <FiCheckCircle aria-hidden />
          <div>
            <span className="scanner-status-card-label">Last completed</span>
            <strong>{formatScannerDate(status?.service?.last_completed_at)}</strong>
          </div>
        </div>
      </div>

      <div className="scanner-toolbar">
        <LoadingButton
          type="button"
          className="btn btn-primary"
          icon={FiPlay}
          loading={runBusy}
          loadingLabel="Starting…"
          onClick={handleRunNow}
          disabled={serviceRunning || !serviceUp}
        >
          Run scan now
        </LoadingButton>
        <button type="button" className="btn btn-secondary" onClick={refreshAll}>
          <FiRefreshCw /> Refresh
        </button>
        {serviceRunning && (
          <span className="scanner-toolbar-hint">
            <FiAlertCircle aria-hidden /> A scan is already running.
          </span>
        )}
      </div>

      <div className="scanner-run-cards">
        <RunDetailCard title="Last run" run={lastRun} icon={FiActivity} />
        <RunDetailCard title="Last successful run" run={lastSuccessfulRun} icon={FiCheckCircle} variant="success" />
      </div>

      <section className="scanner-runs-section">
        <h2>Run history (last 30 days)</h2>
        {runsLoading ? (
          <div className="loading">Loading runs…</div>
        ) : runs.length === 0 ? (
          <p className="scanner-detail-empty">No scan runs yet.</p>
        ) : (
          <div className="scanner-runs-table-wrap">
            <table className="data-table scanner-runs-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Status</th>
                  <th>Trigger</th>
                  <th>Started</th>
                  <th>Completed</th>
                  <th>Inserted</th>
                  <th>Found</th>
                  <th>Filtered</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {pagedRuns.map((run) => {
                  const summary = parseRunSummary(run.summary);
                  return (
                    <tr key={run.id}>
                      <td>#{run.id}</td>
                      <td>
                        <span className={`scanner-status-pill scanner-status-pill--${runStatusClass(run.status)}`}>
                          {run.status}
                        </span>
                      </td>
                      <td>{run.trigger_type}</td>
                      <td>{formatScannerDate(run.started_at || run.created_at)}</td>
                      <td>{formatScannerDate(run.completed_at)}</td>
                      <td>{summary?.inserted ?? '—'}</td>
                      <td>{summary?.total_found ?? '—'}</td>
                      <td>{summary?.passed_filters ?? summary?.filtered_title ?? '—'}</td>
                      <td className="scanner-error-cell">{run.error_message || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <ListPager
          page={runsPagination.page}
          pageCount={runsPagination.pageCount}
          total={runsPagination.total}
          limit={runsPagination.limit}
          onPageChange={setRunsPage}
        />
      </section>
    </div>
  );
};

export default ScannerDashboard;
